import React from 'react';
import { NotificationTypes } from '../../../primary-record-types';
import * as Card from '../../v2BaseComponents/Card';
import { LoadableAvatar, getFallbackFromName } from '../../v2BaseComponents/Avatar';
import urls from '../../../utils/urls';
import getName from '../../../utils/getName';
import Stack from '../../v2BaseComponents/Stack';
import Text from '../../v2BaseComponents/Text';
import getDateString from '../../../utils/getDateString';
import _truncate from 'lodash/truncate';
import Divider from '../../v2BaseComponents/Divider';
import Icon from '../../v2BaseComponents/Icon';
import { View } from 'react-native';
import { useProfilesTabNavigation } from '../../../utils/appNavigationNames';
import NotificationIndicator from '../../v2BaseComponents/NotificationIndicator/NotificationIndicator';
import { Checkbox } from '../../v2BaseComponents/Inputs';

export type OnInAppNotificationPress = (
    medicalProfileId: string,
    inAppNotification: NotificationTypes.ReadOrUnreadInAppNotification,
) => void;

export type InAppNotificationCardOwnProps = {
    inAppNotification: NotificationTypes.ReadOrUnreadInAppNotification;
    medicalProfileId: string;
    flat?: boolean;
    onPress: OnInAppNotificationPress;
    testID?: string;
    isSelected?: boolean;
    isSelectable?: boolean;
    onToggleSelect?: (inAppNotification: NotificationTypes.ReadOrUnreadInAppNotification) => void;
    onMarkAsRead?: (inAppNotification: NotificationTypes.ReadOrUnreadInAppNotification) => void;
};

export type InAppNotificationCardProps = InAppNotificationCardOwnProps;

export const useNavigateOnInAppNotificationPress = () => {
    const { navigate } = useProfilesTabNavigation();

    return (medicalProfileId: string, inAppNotification: NotificationTypes.ReadOrUnreadInAppNotification) => {
        if (inAppNotification.notificationType === 'medical-profile-message') {
            navigate('profile-activity-details', {
                profileId: medicalProfileId,
                auditLogId: inAppNotification.auditLogId,
            });
        } else if (inAppNotification.notificationType === 'medical-profile-activity-comment') {
            navigate('profile-activity-details', {
                profileId: medicalProfileId,
                auditLogId: inAppNotification.auditLogId,
            });
        } else if (inAppNotification.notificationType === 'medical-profile-note-comment') {
            navigate('note', {
                profileId: medicalProfileId,
                noteEntityType: inAppNotification.noteEntityType,
                noteId: inAppNotification.noteId,
            });
        }
    };
};

const InAppNotificationCard: React.FC<InAppNotificationCardProps> = ({
    inAppNotification,
    medicalProfileId,
    flat,
    onPress,
    testID,
    isSelected,
    isSelectable,
    onToggleSelect,
    onMarkAsRead,
}) => {
    const getAvatarUrl = () => {
        if (inAppNotification.notificationType === 'medical-profile-activity-comment') {
            return urls.api.medicalProfiles.inAppNotifications.inAppNotificationAvatar(
                medicalProfileId,
                'medical-profile-activity-comment',
                inAppNotification.auditLogCommentId,
                inAppNotification.userId,
            );
        } else if (inAppNotification.notificationType === 'medical-profile-message') {
            return urls.api.medicalProfiles.inAppNotifications.inAppNotificationAvatar(
                medicalProfileId,
                'medical-profile-message',
                inAppNotification.auditLogId,
                inAppNotification.userId,
            );
        } else if (inAppNotification.notificationType === 'medical-profile-note-comment') {
            return urls.api.medicalProfiles.inAppNotifications.inAppNotificationAvatar(
                medicalProfileId,
                'medical-profile-note-comment',
                inAppNotification.noteCommentId,
                inAppNotification.userId,
            );
        } else {
            throw new Error('Invalid notification type');
        }
    };

    const getMainText = () => {
        const getText = () => {
            if (inAppNotification.notificationType === 'medical-profile-activity-comment') {
                return inAppNotification.commentContent;
            } else if (inAppNotification.notificationType === 'medical-profile-message') {
                return inAppNotification.auditLogTitle;
            } else if (inAppNotification.notificationType === 'medical-profile-note-comment') {
                return inAppNotification.commentContent;
            } else {
                throw new Error('Invalid notification type');
            }
        };

        return <Text variant="body-1">{_truncate(getText(), { length: 100 })}</Text>;
    };

    const getSubContent = () => {
        if (inAppNotification.notificationType === 'medical-profile-message') {
            return null;
        }

        const postText =
            inAppNotification.notificationType === 'medical-profile-note-comment'
                ? inAppNotification.noteTitle
                : inAppNotification.messageContent;

        return (
            <>
                <Divider horizontal />
                <Card.CardPadding>
                    <Card.CardLeftContent
                        leftContent={
                            <View style={{ width: 33, alignItems: 'center' }}>
                                <Icon
                                    name={
                                        inAppNotification.notificationType === 'medical-profile-note-comment'
                                            ? 'Edit2'
                                            : 'Activity'
                                    }
                                    size="card-list-icon"
                                />
                            </View>
                        }
                    >
                        <Card.CardContent>
                            <Text variant="body-1">{postText}</Text>
                        </Card.CardContent>
                    </Card.CardLeftContent>
                </Card.CardPadding>
            </>
        );
    };

    const maybeWrapWithSelect = (children: React.ReactNode) => {
        if (!isSelectable) return children;

        return (
            <Card.CardLeftContent
                leftContent={
                    <View style={{ width: 33, justifyContent: 'center', alignItems: 'center' }}>
                        <Checkbox value={!!isSelected} onChange={() => onToggleSelect?.(inAppNotification)} />
                    </View>
                }
            >
                {children}
            </Card.CardLeftContent>
        );
    };

    const handlePress = () => {
        if (isSelectable) {
            onToggleSelect?.(inAppNotification);
        } else {
            if (!inAppNotification.read) {
                onMarkAsRead?.(inAppNotification);
            }
            onPress(medicalProfileId, inAppNotification);
        }
    };

    return (
        <Card.Card onPress={handlePress} testID={testID || `in-app-notification-card`} flat={flat} disablePadding>
            <Card.CardPadding>
                {maybeWrapWithSelect(
                    <Card.CardLeftContent
                        leftContent={
                            <LoadableAvatar
                                size="sm"
                                fallbackName={getFallbackFromName(
                                    inAppNotification.firstName,
                                    inAppNotification.lastName,
                                )}
                                avatarUrl={getAvatarUrl()}
                            />
                        }
                    >
                        <Card.CardRightContent
                            rightContent={
                                <View style={{ justifyContent: 'flex-start', alignItems: 'center' }}>
                                    <NotificationIndicator shouldHide={inAppNotification.read} />
                                </View>
                            }
                        >
                            <Card.CardContent>
                                <Stack vertical align="flex-start">
                                    <Text variant="body-1" color="Dark-Text">
                                        {getName(inAppNotification)}
                                    </Text>
                                    <Card.CardCaption>
                                        {getDateString(inAppNotification.dateTime, 'date-time')}
                                    </Card.CardCaption>
                                </Stack>
                                {getMainText()}
                            </Card.CardContent>
                        </Card.CardRightContent>
                    </Card.CardLeftContent>,
                )}
            </Card.CardPadding>
            {getSubContent()}
        </Card.Card>
    );
};

export default React.memo(InAppNotificationCard);
